import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  styled,
  Badge,
  Tooltip,
  useTheme
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TodayIcon from '@mui/icons-material/Today';
import EventIcon from '@mui/icons-material/Event';
import BlockIcon from '@mui/icons-material/Block';
import { format } from 'date-fns';
import { ShineBorder } from '../ui/ShineBorder';
import { MagicCard } from '../ui/MagicCard';
import { Meteors } from '../ui/Meteors';

const CalendarHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2, 0),
  borderBottom: `2px solid ${theme.palette.primary.main}`,
}));

const DayCell = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  height: '100%',
  minHeight: '100px',
  display: 'flex',
  flexDirection: 'column',
  cursor: 'pointer',
  position: 'relative',  // For absolute positioning of appointment badge
  transition: 'all 0.2s ease-in-out',
  border: '1px solid transparent',
  boxShadow: 'none',
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-3px)',
    boxShadow: theme.shadows[3],
  },
}));

const DayNumber = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.text.secondary,
}));

import { CalendarEvent } from '../../models/calendar';

interface CalendarProps {
  onDateSelect?: (date: Date) => void;
  onMonthChange?: (date: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  onTimeSlotClick?: (date: Date, practitionerId?: string) => void;
  events?: CalendarEvent[];
  initialDate?: Date;
}

const Calendar: React.FC<CalendarProps> = ({ 
  onDateSelect, 
  onMonthChange,
  onEventClick,
  onTimeSlotClick,
  events = [],
  initialDate
}) => {
  const theme = useTheme();
  const [currentDate, setCurrentDate] = useState(initialDate || new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Get the first day of the month
  const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  
  // Get the last day of the month
  const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  
  // Get the day of the week for the first day (0 = Sunday, 1 = Monday, etc.)
  const firstDayOfWeek = firstDay.getDay();
  
  // Calculate how many days to show from the previous month
  const daysFromPrevMonth = firstDayOfWeek;
  
  // Calculate the first date to show in the calendar
  const firstDateToShow = new Date(firstDay);
  firstDateToShow.setDate(firstDateToShow.getDate() - daysFromPrevMonth);
  
  // Generate the dates to display (42 days = 6 weeks)
  const daysToShow = 42;
  const dates = Array.from({ length: daysToShow }, (_, i) => {
    const date = new Date(firstDateToShow);
    date.setDate(date.getDate() + i);
    return date;
  });

  // useEffect to call onMonthChange when currentDate changes
  useEffect(() => {
    if (onMonthChange) {
      onMonthChange(currentDate);
    }
  }, [currentDate, onMonthChange]);

  // Navigate to previous month
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  // Navigate to next month
  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  // Navigate to today
  const handleToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
    if (onDateSelect) {
      onDateSelect(today);
    }
  };

  // Handle date selection
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    if (onDateSelect) {
      onDateSelect(date);
    }
    
    // If onTimeSlotClick is provided, call it with the current date
    // This will open the appointment form on date click
    if (onTimeSlotClick) {
      const clickTime = new Date(date);
      // Keep current time if it's during business hours, otherwise use 9 AM
      const currentHour = clickTime.getHours();
      if (currentHour < 8 || currentHour > 20) {
        // Set a reasonable default time (e.g., 9:00 AM) if outside business hours
        clickTime.setHours(9, 0, 0, 0);
      }
      onTimeSlotClick(clickTime);
    }
  };

  // Check if a date is in the current month
  const isCurrentMonth = (date: Date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  // Check if a date is today
  const isToday = (date: Date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Check if a date is selected
  const isSelected = (date: Date) => {
    if (!selectedDate) return false;
    return date.getDate() === selectedDate.getDate() &&
           date.getMonth() === selectedDate.getMonth() &&
           date.getFullYear() === selectedDate.getFullYear();
  };

  // Get the events for a specific date from the events prop
  const getEventsForDate = (date: Date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start);
      return (
        eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear()
      );
    });
  };
  
  // Get appointment count for a date
  const getAppointmentCountForDate = (date: Date) => {
    return getEventsForDate(date).filter(event => event.type === 'appointment').length;
  };
  
  // Get unavailable blocks for a date
  const getUnavailableBlocksForDate = (date: Date) => {
    return getEventsForDate(date).filter(event => event.type === 'unavailable');
  };
  
  // Check if a date has unavailable blocks
  const hasUnavailableBlockOnDate = (date: Date) => {
    return getEventsForDate(date).some(event => event.type === 'unavailable');
  };
  
  // Handle event click
  const handleEventClick = (event: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent date selection when clicking on an event
    if (onEventClick) {
      onEventClick(event);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: theme.shape.borderRadius, pb: 1 }}>
        <Meteors 
          number={5}
          minDuration={4}
          maxDuration={8}
          angle={205}
        />
        <CalendarHeader sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ position: 'relative' }}>
            <Typography variant="h5" component="h2" fontWeight="bold" sx={{
              color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main',
              textShadow: theme.palette.mode === 'dark' ? '0 1px 3px rgba(0,0,0,0.3)' : 'none'
            }}>
              {format(currentDate, 'MMMM yyyy')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: theme.shape.borderRadius }}>
              <Button
                startIcon={<TodayIcon />}
                onClick={handleToday}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main',
                  color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main',
                  '&:hover': {
                    borderColor: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.15)' : 'rgba(25, 118, 210, 0.08)'
                  },
                  position: 'relative',
                  zIndex: 1
                }}
              >
                Today
              </Button>
              <ShineBorder 
                borderWidth={2} 
                shineColor={[
                  theme.palette.primary.main, 
                  theme.palette.secondary.main, 
                  theme.palette.primary.light
                ]}
              />
            </Box>
            <IconButton onClick={handlePrevMonth} sx={{
              color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <ArrowBackIosNewIcon fontSize="small" />
              <ShineBorder 
                borderWidth={1} 
                shineColor={theme.palette.primary.main}
              />
            </IconButton>
            <IconButton onClick={handleNextMonth} sx={{
              color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <ArrowForwardIosIcon fontSize="small" />
              <ShineBorder 
                borderWidth={1} 
                shineColor={theme.palette.primary.main}
              />
            </IconButton>
          </Box>
        </CalendarHeader>
        <ShineBorder 
          borderWidth={1} 
          shineColor={[
            theme.palette.primary.main, 
            theme.palette.secondary.main, 
            theme.palette.primary.light
          ]}
        />
      </Box>

      <Grid container spacing={2}>
        {/* Weekday headers */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <Grid item xs={12/7} key={day}>
            <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: '8px 8px 0 0' }}>
              <Box 
                sx={{ 
                  py: 1.5, 
                  bgcolor: 'primary.main', 
                  color: 'primary.contrastText',
                  borderRadius: '8px 8px 0 0',
                  mb: 1,
                  boxShadow: theme.palette.mode === 'dark' ? '0 2px 4px rgba(0,0,0,0.3)' : 'none',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                <Typography variant="subtitle2" align="center" fontWeight="bold">
                  {day}
                </Typography>
              </Box>
              <ShineBorder 
                borderWidth={1} 
                shineColor={[theme.palette.primary.light, theme.palette.secondary.main]}
                duration={10}
              />
            </Box>
          </Grid>
        ))}

        {/* Calendar days */}
        {dates.map((date, index) => {
          const appointmentCount = getAppointmentCountForDate(date);
          const hasAppointments = appointmentCount > 0;
          const hasUnavailableBlock = hasUnavailableBlockOnDate(date);
          
          return (
            <Grid item xs={12/7} key={index}>
              <Tooltip 
                title={
                  hasAppointments 
                    ? `${appointmentCount} appointment${appointmentCount !== 1 ? 's' : ''}${hasUnavailableBlock ? ' • Has unavailable blocks' : ''}` 
                    : hasUnavailableBlock 
                      ? 'Has unavailable blocks' 
                      : ''
                } 
                arrow
                placement="top"
                disableHoverListener={!hasAppointments && !hasUnavailableBlock}
              >
                <MagicCard
                  className={isCurrentMonth(date) ? undefined : 'opacity-50'}
                  gradientFrom={theme.palette.primary.main}
                  gradientTo={theme.palette.secondary.main}
                  gradientOpacity={isSelected(date) ? 0.8 : 0.4}
                >
                <DayCell 
                  elevation={isSelected(date) ? 3 : 0}
                  sx={{
                    bgcolor: isSelected(date) ? 'primary.light' : 
                            isToday(date) ? 'info.light' : 
                            hasUnavailableBlock ? 
                              theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.08)' :
                            hasAppointments ? 
                              theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.08)' : 
                            'background.paper',
                    color: isSelected(date) ? 'primary.contrastText' : 'inherit',
                    position: 'relative',
                    zIndex: 1
                  }}
                  onClick={() => handleDateSelect(date)}
                >
                  <DayNumber variant="body2" align="right">
                    {date.getDate()}
                  </DayNumber>
                  
                  {/* Display unavailable blocks with consolidated time ranges */}
                  {hasUnavailableBlock && (
                    <Box sx={{ mt: 1, mb: 1 }}>
                      {(() => {
                        // Group unavailable blocks by practitioner and merge overlapping times
                        const blocksByPractitioner = getUnavailableBlocksForDate(date).reduce((acc, block) => {
                          const practitionerId = block.practitionerId;
                          if (!acc[practitionerId]) {
                            acc[practitionerId] = [];
                          }
                          acc[practitionerId].push(block);
                          return acc;
                        }, {} as Record<string, typeof block[]>);

                        // For each practitioner, create one consolidated block
                        return Object.entries(blocksByPractitioner).map(([practitionerId, blocks]) => {
                          // Sort blocks by start time
                          const sortedBlocks = blocks.sort((a, b) => a.start.getTime() - b.start.getTime());
                          
                          // Find the earliest start and latest end time
                          const earliestStart = sortedBlocks[0].start;
                          const latestEnd = sortedBlocks[sortedBlocks.length - 1].end;
                          
                          // Use the first block for other properties
                          const firstBlock = sortedBlocks[0];
                          
                          return (
                            <Box 
                              key={`consolidated-block-${practitionerId}`}
                              onClick={(e) => handleEventClick(firstBlock, e)}
                              sx={{ 
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.15)',
                                p: 0.5,
                                borderRadius: 1,
                                mb: 0.5,
                                fontSize: '0.7rem',
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                border: theme.palette.mode === 'dark' ? '1px solid rgba(244, 67, 54, 0.5)' : 'none',
                                '&:hover': {
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.5)' : 'rgba(244, 67, 54, 0.3)',
                                }
                              }}
                            >
                              <BlockIcon 
                                sx={{ 
                                  fontSize: '0.8rem', 
                                  mr: 0.5,
                                  color: theme.palette.mode === 'dark' ? '#ff6b6b' : 'error.main'
                                }} 
                              />
                              <Typography variant="caption" sx={{ 
                                color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                                fontWeight: 'medium',
                                lineHeight: 1.2
                              }}>
                                Unavailable
                                {firstBlock.practitionerName && (
                                  <>
                                    <br />
                                    {firstBlock.practitionerName}
                                  </>
                                )}
                                <br />
                                {earliestStart.toLocaleTimeString('en-AU', { 
                                  hour: 'numeric', 
                                  minute: '2-digit',
                                  hour12: true
                                })} - {latestEnd.toLocaleTimeString('en-AU', { 
                                  hour: 'numeric', 
                                  minute: '2-digit',
                                  hour12: true
                                })}
                              </Typography>
                            </Box>
                          );
                        });
                      })()}
                    </Box>
                  )}
                  
                  {/* Display appointments with time if any exist */}
                  {hasAppointments && (
                    <Box sx={{ mt: 1, mb: 1 }}>
                      {getEventsForDate(date)
                        .filter(event => event.type === 'appointment')
                        .sort((a, b) => a.start.getTime() - b.start.getTime())
                        .map((appointment, idx) => (
                          <Box 
                            key={`appointment-${idx}`}
                            onClick={(e) => handleEventClick(appointment, e)}
                            sx={{ 
                              bgcolor: appointment.cancelled ? 
                                theme.palette.mode === 'dark' ? 'rgba(158, 158, 158, 0.3)' : 'rgba(158, 158, 158, 0.15)' :
                                // Use the color property from the appointment, with fallback to default blue
                                appointment.color ? 
                                  theme.palette.mode === 'dark' ? 
                                    `${appointment.color}40` : // 25% opacity in hex
                                    `${appointment.color}20` : // 12% opacity in hex
                                  theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.3)' : 'rgba(25, 118, 210, 0.15)',
                              p: 0.5,
                              borderRadius: 1,
                              mb: 0.5,
                              fontSize: '0.7rem',
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer',
                              borderLeft: appointment.cancelled ? 
                                '3px solid #9e9e9e' : 
                                appointment.color ? 
                                  `3px solid ${appointment.color}` : 
                                  '3px solid #1976d2',
                              '&:hover': {
                                bgcolor: appointment.cancelled ? 
                                  theme.palette.mode === 'dark' ? 'rgba(158, 158, 158, 0.5)' : 'rgba(158, 158, 158, 0.3)' :
                                  appointment.color ? 
                                    theme.palette.mode === 'dark' ? 
                                      `${appointment.color}60` : // 38% opacity
                                      `${appointment.color}40` : // 25% opacity
                                    theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.5)' : 'rgba(25, 118, 210, 0.3)',
                              }
                            }}
                          >
                            <EventIcon 
                              sx={{ 
                                fontSize: '0.8rem', 
                                mr: 0.5,
                                color: appointment.cancelled ? 
                                  theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : 'text.disabled' :
                                  theme.palette.mode === 'dark' ? '#90caf9' : 'primary.main'
                              }} 
                            />
                            <Typography variant="caption" sx={{ 
                              color: theme.palette.mode === 'dark' ? 
                                appointment.cancelled ? 'rgba(255, 255, 255, 0.5)' : '#ffffff' : 
                                appointment.cancelled ? 'text.disabled' : 'inherit',
                              fontWeight: 'medium'
                            }}>
                              {new Date(appointment.start).toLocaleTimeString('en-AU', { 
                                hour: 'numeric', 
                                minute: '2-digit',
                                hour12: true
                              })}
                              {appointment.patientName ? ` - ${appointment.patientName}` : ''}
                            </Typography>
                          </Box>
                        ))}
                    </Box>
                  )}

                  <Box sx={{ position: 'absolute', bottom: 5, right: 5, display: 'flex', gap: 1 }}>
                    {hasUnavailableBlock && !isSelected(date) && (
                      <Box 
                        sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <BlockIcon 
                          fontSize="small" 
                          sx={{ 
                            color: theme.palette.mode === 'dark' ? '#ff6b6b' : 'error.main',
                            opacity: theme.palette.mode === 'dark' ? 1 : 0.7,
                            filter: theme.palette.mode === 'dark' ? 'drop-shadow(0 0 2px rgba(255,0,0,0.3))' : 'none'
                          }} 
                        />
                      </Box>
                    )}
                    
                    {hasAppointments && (
                      <Box 
                        sx={{ 
                          bgcolor: 'primary.main', 
                          color: 'primary.contrastText',
                          borderRadius: '50%',
                          width: 24,
                          height: 24,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          boxShadow: theme.palette.mode === 'dark' ? 
                            '0 2px 6px rgba(0,0,0,0.4), 0 0 0 2px rgba(25, 118, 210, 0.3)' : 
                            '0 2px 6px rgba(0,0,0,0.2), 0 0 0 2px white',
                          animation: 'pulse 1.5s infinite',
                          '@keyframes pulse': {
                            '0%': {
                              boxShadow: theme.palette.mode === 'dark' ? 
                                '0 0 0 0 rgba(25, 118, 210, 0.7), 0 0 0 2px rgba(25, 118, 210, 0.3)' : 
                                '0 0 0 0 rgba(25, 118, 210, 0.4), 0 0 0 2px white'
                            },
                            '70%': {
                              boxShadow: theme.palette.mode === 'dark' ? 
                                '0 0 0 6px rgba(25, 118, 210, 0), 0 0 0 2px rgba(25, 118, 210, 0.3)' : 
                                '0 0 0 6px rgba(25, 118, 210, 0), 0 0 0 2px white'
                            },
                            '100%': {
                              boxShadow: theme.palette.mode === 'dark' ? 
                                '0 0 0 0 rgba(25, 118, 210, 0), 0 0 0 2px rgba(25, 118, 210, 0.3)' : 
                                '0 0 0 0 rgba(25, 118, 210, 0), 0 0 0 2px white'
                            }
                          }
                        }}
                      >
                        {appointmentCount}
                      </Box>
                    )}
                  </Box>
                </DayCell>
                </MagicCard>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default Calendar;